const CONTINUATION_KEY = 'paddiq:chatgpt-connection:v1';
const REQUEST_ID_PATTERN = /^pmc_p_[A-Za-z0-9_-]{43}$/;
const CONTINUATION_LIFETIME_MS = 10 * 60 * 1_000;

type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

export function parseChatGptRequestId(search: string): string | null {
  const params = new URLSearchParams(search);
  const requestId = params.get('request');
  return params.getAll('request').length === 1 && requestId && REQUEST_ID_PATTERN.test(requestId)
    ? requestId
    : null;
}

export function chatGptConnectionPath(requestId: string): string | null {
  return REQUEST_ID_PATTERN.test(requestId)
    ? `/connect/chatgpt?${new URLSearchParams({ request: requestId }).toString()}`
    : null;
}

export function storeChatGptContinuation(
  requestId: string,
  storage: StorageLike,
  now = Date.now(),
) {
  if (!chatGptConnectionPath(requestId)) return;
  // Only the pending request identifier is retained, never OAuth or account tokens.
  storage.setItem(
    CONTINUATION_KEY,
    JSON.stringify({ requestId, expiresAt: now + CONTINUATION_LIFETIME_MS }),
  );
}

export function clearChatGptContinuation(storage: StorageLike) {
  storage.removeItem(CONTINUATION_KEY);
}

export function readChatGptContinuation(storage: StorageLike, now = Date.now()): string | null {
  const value = storage.getItem(CONTINUATION_KEY);
  if (!value) return null;
  try {
    const parsed: unknown = JSON.parse(value);
    if (!parsed || typeof parsed !== 'object') throw new Error('Invalid continuation');
    const record = parsed as { requestId?: unknown; expiresAt?: unknown };
    if (
      Object.keys(record).length !== 2 ||
      typeof record.requestId !== 'string' ||
      typeof record.expiresAt !== 'number' ||
      !Number.isFinite(record.expiresAt) ||
      record.expiresAt <= now ||
      record.expiresAt > now + CONTINUATION_LIFETIME_MS
    )
      throw new Error('Expired or invalid continuation');
    const destination = chatGptConnectionPath(record.requestId);
    if (!destination) throw new Error('Invalid request');
    return destination;
  } catch {
    clearChatGptContinuation(storage);
    return null;
  }
}

export function connectionSessionRestriction(session: {
  sessionKind: string | null;
  supportSession: unknown;
  returnToKiosk?: boolean;
}): string | null {
  if (session.sessionKind === 'kiosk' || session.returnToKiosk) {
    return 'App connections are unavailable on shared kiosks. Sign in to Paddiq on your own device to manage connections.';
  }
  if (session.supportSession) {
    return 'App connections are unavailable while masquerading as a customer. Return to your own account to manage connections.';
  }
  return null;
}
